import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Brand } from "@/components/layout/Brand";
import { scrollToId } from "@/lib/scroll";
import { SERVICE_CATEGORIES } from "@/data/services";

const NAV_LINKS = [
  { name: "Home", href: "/" },
  { name: "Services", href: "/services" },
  { name: "Pricing", href: "/pricing" },
  { name: "Training", href: "/training" },
  { name: "Careers", href: "/careers" },
  { name: "Blog", href: "/blog" },
  { name: "Contact", href: "/contact" },
];

// Submenu links jump to a category or card on /services. On the Services
// page itself the URL's path doesn't change, so App's ScrollToTop doesn't
// fire; scroll there directly instead.
function useSectionLink(onNavigate: () => void) {
  const [location] = useLocation();
  return (id: string) => () => {
    onNavigate();
    if (location === "/services") scrollToId(id, "smooth");
  };
}

/** Sticky site header, as in the redesign mockup (`.nav`, `.mega` etc. in design.css). */
export function Navbar() {
  const [location] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Section links stay highlighted on their sub-pages (e.g. a blog post).
  const isActive = (href: string) =>
    location === href || (href !== "/" && location.startsWith(`${href}/`));

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const goToSection = useSectionLink(() => {
    setMenuOpen(false);
    // Move focus out of the menu so :focus-within doesn't keep it open.
    (document.activeElement as HTMLElement | null)?.blur();
  });

  // The mockup's translucent, blurred bar suits the homepage, which is
  // mostly dark. On the inner pages long white sections smear through it,
  // so there it's solid ink navy.
  const solid = location !== "/";

  return (
    <header
      className="nav"
      style={solid ? { background: "#070B16", backdropFilter: "none", WebkitBackdropFilter: "none" } : undefined}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 32px", height: "76px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "24px" }}>
        <Brand />

        <nav className="navlinks" aria-label="Main" style={{ display: "flex", alignItems: "center", gap: "22px", fontSize: "15px" }}>
          {NAV_LINKS.map((link) =>
            link.href === "/services" ? (
              <div key={link.name} className="dd">
                <Link
                  className="nl"
                  href={link.href}
                  aria-current={isActive(link.href) ? "page" : undefined}
                  style={{ display: "inline-flex", alignItems: "center", gap: "6px", ...(isActive(link.href) ? { color: "#fff" } : {}) }}
                >
                  Services
                  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"></path></svg>
                </Link>
                <div className="mega">
                  <div style={{ background: "#0D1424", border: "1px solid rgba(255,255,255,.09)", borderRadius: "18px", padding: "28px", boxShadow: "0 40px 80px -20px rgba(0,0,0,.6)" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "28px" }}>
                      {SERVICE_CATEGORIES.map((category) => (
                        <div key={category.id}>
                          <Link
                            href={`/services#${category.id}`}
                            onClick={goToSection(category.id)}
                            style={{ fontFamily: "Sora, sans-serif", fontWeight: "600", fontSize: "14px", color: "#fff", display: "block", marginBottom: "10px" }}
                          >
                            {category.label}
                          </Link>
                          {category.services.map((service) => (
                            <Link
                              key={service.anchor}
                              className="ml"
                              href={`/services#${service.anchor}`}
                              onClick={goToSection(service.anchor)}
                            >
                              {service.title}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: "22px", paddingTop: "18px", borderTop: "1px solid rgba(255,255,255,.08)", display: "flex", justifyContent: "space-between", fontSize: "14px" }}>
                      <Link className="lnk" href="/services" style={{ color: "#9CB0FF" }}>View all services →</Link>
                      <Link className="ml" href="/pricing" style={{ padding: "0" }}>See website pricing</Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={link.name}
                className="nl"
                href={link.href}
                aria-current={isActive(link.href) ? "page" : undefined}
                style={isActive(link.href) ? { color: "#fff" } : undefined}
              >
                {link.name}
              </Link>
            ),
          )}
        </nav>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Link className="btn btn-p navcta" href="/contact" style={{ padding: "12px 20px", borderRadius: "999px", fontWeight: "600", fontSize: "14px" }}>Talk to Our Team</Link>
          <button
            type="button"
            className="menubtn"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            style={{ width: "46px", height: "46px", borderRadius: "12px", border: "1px solid rgba(255,255,255,.18)", background: "transparent", color: "#fff", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true"><path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"></path></svg>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div id="mobile-nav" style={{ borderTop: "1px solid rgba(255,255,255,.08)", padding: "16px 24px 28px", display: "flex", flexDirection: "column", gap: "4px", fontSize: "17px", animation: "fadein .3s" }}>
          {NAV_LINKS.map((link) => (
            <Link
              key={link.name}
              className="ml"
              href={link.href}
              aria-current={isActive(link.href) ? "page" : undefined}
              style={{ padding: "10px 0", ...(isActive(link.href) ? { color: "#fff" } : {}) }}
            >
              {link.name}
            </Link>
          ))}
          <Link className="btn btn-p" href="/contact" style={{ marginTop: "12px", padding: "14px 20px", borderRadius: "999px", fontWeight: "600", justifyContent: "center" }}>Talk to Our Team</Link>
        </div>
      )}
    </header>
  );
}
